import React, { useState, useRef, DragEvent, ClipboardEvent, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid'; // For generating unique IDs
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useOktaAuth } from '@okta/okta-react';
import { useSearch } from '../../context/SearchContext';
import { IProductSummaryResponse } from '../../models/iProductSearchRequest';
import { openGeminiResultsWindow } from '../../utils/openGeminiResultsWindow';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

interface PastedFile {
  id: string; // Unique ID for each file
  file: File;
}

export interface ITextAndFilePasteInputProps {
  onSubmit: (text: string, files: File[]) => Promise<void>;
  onGeminiSubmit?: (text: string, files: File[]) => Promise<IProductSummaryResponse>;
  disabled?: boolean;
}

/**
 * TextAndFilePasteInput component allows users to paste text and files into a text input.
 * It handles pasting files from the clipboard, displaying them below the input,
 * and allows users to remove files if needed.
 */
const TextAndFilePasteInput: React.FC<ITextAndFilePasteInputProps> = ({ onSubmit, onGeminiSubmit, disabled }) => {
  const [textValue, setTextValue] = useState<string>('');
  const [pastedFiles, setPastedFiles] = useState<PastedFile[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [geminiLoading, setGeminiLoading] = useState<boolean>(false);
  const { state } = useSearch();
  const { searchResetTrigger } = state;
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const { authState } = useOktaAuth();

  const bearerToken = authState?.accessToken?.accessToken || '';
  const featureFlagsQuery = useFeatureFlags(bearerToken);
  const displayGoogleSearchButton = featureFlagsQuery.data?.googleSearchButton === true;

  useEffect(() => {
    // Don't clear on the initial render (when the trigger is 0)
    if (searchResetTrigger > 0) {
      setTextValue('');
      setPastedFiles([]);
    }
  }, [searchResetTrigger]);

  /**
   * Add files to the pasted files state and update the text value.
   * This function generates a unique ID for each file and adds it to the state.
   * @param files
   */
  const addFilesToState = (files: File[]) => {
    const newPastedFiles: PastedFile[] = [];
    let filePlaceholderText = '';

    files.forEach((file) => {
      const newFile: PastedFile = {
        id: uuidv4(), // Generate a unique ID
        file: file,
      };
      newPastedFiles.push(newFile);
    });

    setPastedFiles((prevFiles) => [...prevFiles, ...newPastedFiles]);
    setTextValue((prev) => prev + filePlaceholderText.trim());
  };

  /**
   * Handle the paste event to extract files from the clipboard.
   * @param event
   */
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (items) {
      if (!disabled) {
        const filesToProcess: File[] = [];

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              filesToProcess.push(file);
            }
          }
        }

        if (filesToProcess.length > 0) {
          addFilesToState(filesToProcess);
        }
      }
    } else {
      toast.warn('Please first select a sales org and product group to enable this feature.');
    }
  };

  /**
   * Handle the change event for the text input.
   * @param event
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextValue(event.target.value);
  };

  /**
   * Handle the drop event to add files to the pasted files state.
   * @param event
   */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      if (!disabled) {
        const filesArray = Array.from(files);
        addFilesToState(filesArray);
      } else {
        toast.warn('Please first select a sales org and product group to enable this feature.');
      }
    }
  };

  /**
   *
   * @param event
   */
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  /**
   * Remove the file with the given ID from the pasted files state.
   * @param fileIdToRemove
   * @returns
   */
  const removeFile = (fileIdToRemove: string) => {
    const fileToRemove = pastedFiles.find((file) => file.id === fileIdToRemove);
    if (!fileToRemove) {
      return;
    }

    setPastedFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileIdToRemove));
  };

  const handleApiSubmit = async () => {
    setIsLoading(true);
    try {
      const files = pastedFiles.map((file) => file.file);
      await onSubmit(textValue, files);
    } catch (error) {
      toast.error('Failed to submit data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeminiSubmit = async () => {
    if (!onGeminiSubmit) return;

    setGeminiLoading(true);
    try {
      const files = pastedFiles.map((file) => file.file);
      const result = await onGeminiSubmit(textValue, files);

      // Open results in a new window using the utility function
      try {
        openGeminiResultsWindow(result);
      } catch (windowError) {
        toast.error('Unable to open new window with search results. Please check your popup blocker settings.');
      }
    } catch (error) {
      toast.error('Failed to get Gemini summary. Please try again later.');
    } finally {
      setGeminiLoading(false);
    }
  };

  /**
   * Render the input box for pasting text and files.
   * It includes a text field for input and displays any pasted files below it.
   */
  return (
    <Stack
      direction={'column'}
      padding={2}
      sx={{
        width: '100%',
        flex: 1,
        height: '100%',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          textAlign: 'left',
          alignSelf: 'flex-start',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        Search with Text and/or Images
      </Typography>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: '2px grey',
          padding: 2,
          borderRadius: 1,
          width: '100%',
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
        }}
      >
        <TextField
          label={disabled ? 'Please Select One Product Group' : 'Paste Text or Files Here'}
          multiline
          rows={4}
          fullWidth
          value={textValue}
          onChange={handleInputChange}
          onPaste={handlePaste}
          inputRef={inputRef}
          disabled={disabled}
          variant="outlined"
          placeholder="You can type, paste text, paste or drop files."
          sx={{ flexGrow: 1 }}
          InputProps={{
            style: { flexGrow: 1, height: '100%' },
            disabled: disabled,
          }}
        />
        {pastedFiles.length > 0 && (
          <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Attached Files:
            </Typography>
            {pastedFiles.map((file) => (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1,
                  p: 1,
                  border: '1px solid #eee',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InsertDriveFileIcon sx={{ mr: 1, color: 'action.active' }} />
                  <Typography variant="body2">
                    {file.file.name} ({file.file.type}, {(file.file.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Box>
                <IconButton aria-label="delete file" size="small" onClick={() => removeFile(file.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>
        )}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: isXlUp ? 'row' : 'column',
            justifyContent: isXlUp ? 'space-between' : 'stretch',
            alignItems: isXlUp ? 'center' : 'stretch',
            gap: isXlUp ? 0 : 2,
          }}
        >
          {/* Left side - Gemini Search Button */}
          {onGeminiSubmit && displayGoogleSearchButton && (
            <Button
              variant="contained"
              color="info"
              onClick={handleGeminiSubmit}
              disabled={geminiLoading || disabled || (textValue.trim() === '' && pastedFiles.length === 0)}
              startIcon={geminiLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{
                minWidth: 140,
                width: isXlUp ? 'auto' : '100%',
                '&:disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {geminiLoading ? 'Searching...' : 'Gemini Search'}
            </Button>
          )}

          {/* Spacer for when Gemini button is not available on larger screens */}
          {(!onGeminiSubmit || !displayGoogleSearchButton) && isXlUp && <Box />}

          {/* Right side - Existing Evaluate Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleApiSubmit}
            disabled={isLoading || (textValue.trim() === '' && pastedFiles.length === 0)}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              width: isXlUp ? 'auto' : '100%',
            }}
          >
            {isLoading ? 'Sending...' : 'Evaluate customer request'}
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default TextAndFilePasteInput;
